import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { SideDrawerComponent } from "../shell/side-drawer.component";
import {
  ANCHOR_LABELS,
  CrewCommandsStore,
  type AnchorState,
} from "../core/sim/crew-commands.store";
import { SimService } from "../core/sim/sim.service";

/**
 * Anchor drawer. Two main commands — drop / weigh — plus a chain-length
 * slider used at drop time. As with all crew commands, dropping or
 * weighing takes 2–5 s of "deck time" (random per call).
 *
 * Once dropped, the slider becomes read-only — to change scope the
 * crew must weigh first. Mirrors typical foredeck workflow.
 */
@Component({
  selector: "po-anchor-drawer",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SideDrawerComponent],
  templateUrl: "./anchor-drawer.component.html",
  styleUrl: "./anchor-drawer.component.css",
})
export class AnchorDrawerComponent {
  readonly open = input.required<boolean>();
  readonly anchorBottom = input<number>(64);
  readonly closeRequest = output<void>();

  readonly store = inject(CrewCommandsStore);
  private readonly sim = inject(SimService);

  readonly state = computed(() => this.store.anchor());
  readonly anchorEquipment = computed(() => this.sim.playerAnchorEquipment());
  readonly maxRodeLength = computed(() =>
    Math.max(5, Math.floor(this.anchorEquipment().anchorRodeMaxLength)),
  );
  readonly boundedTargetRode = computed(() =>
    Math.min(this.targetChain(), this.maxRodeLength()),
  );
  readonly rodeLabel = computed(() =>
    this.anchorEquipment().anchorRodeType === "chain"
      ? "Łańcuch"
      : this.anchorEquipment().anchorRodeType === "mixed"
        ? "Lina + łańcuch"
        : "Lina",
  );
  readonly rodeGenitiveLabel = computed(() =>
    this.anchorEquipment().anchorRodeType === "chain"
      ? "łańcucha"
      : this.anchorEquipment().anchorRodeType === "mixed"
        ? "liny z łańcuchem"
        : "liny",
  );
  readonly rodePayOutLabel = computed(
    () => `Długość ${this.rodeGenitiveLabel()} do wydania`,
  );
  readonly rodeWeightLabel = computed(
    () => `${this.anchorEquipment().anchorRodeWeightKgPerM.toFixed(2)} kg/m`,
  );
  readonly depthLabel = computed(() =>
    this.anchorEquipment().hasDepthSounder ? "Echosonda" : "Głębokość",
  );
  readonly depthInputLabel = computed(() =>
    this.anchorEquipment().hasDepthSounder
      ? "Głębokość z echosondy"
      : "Szacowana głębokość",
  );
  /** UI-side desired chain length, applied when drop is invoked. */
  readonly targetChain = signal(20);
  readonly targetDepth = signal(3);
  readonly scopeRatio = computed(
    () => this.boundedTargetRode() / Math.max(0.5, this.targetDepth() + 0.8),
  );

  stateLabel(s: AnchorState): string {
    return ANCHOR_LABELS[s];
  }

  setTarget(event: Event): void {
    const v = Number((event.target as HTMLInputElement).value);
    this.targetChain.set(Math.min(v, this.maxRodeLength()));
  }

  setDepth(event: Event): void {
    const v = Number((event.target as HTMLInputElement).value);
    this.targetDepth.set(v);
  }
}
